const DESKTOP_LAST_ROUTE_KEY = "forge:desktop:last-route:v1";
const DESKTOP_PRIMARY_CLOUD_ENV_PLACEHOLDER = "__desktop-primary__";
const DESKTOP_RESERVED_ROUTE_SEGMENTS = new Set([
  "admin",
  "cmmd-center",
  "draft",
  "login",
  "new",
  "pair",
  "project",
  "settings",
]);

type ParsedDesktopRestorableRoute =
  | {
      kind: "raw";
      hash: string;
    }
  | {
      kind: "thread";
      environmentId: string;
      threadId: string;
      search: string;
    }
  | {
      kind: "project";
      environmentId: string;
      projectId: string;
      search: string;
    };

function isDesktopRenderer(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.desktopBridge !== undefined || window.nativeApi !== undefined)
  );
}

function readDesktopPrimaryCloudEnvironmentId(): string | null {
  if (!isDesktopRenderer() || typeof window === "undefined") {
    return null;
  }
  const bootstrap = window.desktopBridge?.getLocalEnvironmentBootstrap?.();
  if (bootstrap?.primaryMode !== "cloud") {
    return null;
  }
  if (typeof bootstrap.environmentId !== "string") {
    return null;
  }
  const environmentId = bootstrap.environmentId.trim();
  return environmentId.length > 0 ? environmentId : null;
}

function isKnownDesktopSavedEnvironmentId(environmentId: string): boolean {
  if (!isDesktopRenderer() || typeof window === "undefined") {
    return false;
  }
  const records = window.desktopBridge?.getSavedEnvironmentRegistrySync?.() ?? [];
  return records.some((record) => record.environmentId === environmentId);
}

function normalizeDesktopRestorableHash(hash: string | null | undefined): string | null {
  if (!hash) {
    return null;
  }
  const trimmed = hash.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const normalized = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  const route = normalized.slice(1);
  if (!route.startsWith("/")) {
    return null;
  }
  if (
    route === "/" ||
    route === "/login" ||
    route.startsWith("/login?") ||
    route === "/onboarding" ||
    route.startsWith("/onboarding?") ||
    route === "/pair" ||
    route.startsWith("/pair?") ||
    route === "/settings" ||
    route.startsWith("/settings/")
  ) {
    return null;
  }
  return normalized;
}

function parseDesktopRestorableRoute(
  hash: string | null | undefined,
): ParsedDesktopRestorableRoute | null {
  const normalized = normalizeDesktopRestorableHash(hash);
  if (!normalized) {
    return null;
  }
  const routeUrl = new URL(normalized.slice(1), "https://forge.invalid");
  const segments = routeUrl.pathname.split("/").filter(Boolean);

  if (segments[0] === "project" && segments[1] && segments[2]) {
    return {
      kind: "project",
      environmentId: segments[1],
      projectId: segments[2],
      search: routeUrl.search,
    };
  }

  if (
    segments[0] &&
    segments[1] &&
    !DESKTOP_RESERVED_ROUTE_SEGMENTS.has(segments[0]) &&
    segments.length === 2
  ) {
    return {
      kind: "thread",
      environmentId: segments[0],
      threadId: segments[1],
      search: routeUrl.search,
    };
  }

  return {
    kind: "raw",
    hash: normalized,
  };
}

function stringifyDesktopRestorableRoute(route: ParsedDesktopRestorableRoute): string {
  switch (route.kind) {
    case "raw":
      return route.hash;
    case "thread":
      return `#/${route.environmentId}/${route.threadId}${route.search}`;
    case "project":
      return `#/project/${route.environmentId}/${route.projectId}${route.search}`;
  }
}

function hasBootstrappedDesktopHash(hash: string | null | undefined): boolean {
  return hash === "#" || hash === "#/" || hash === null || hash === undefined || hash === "";
}

function normalizeDesktopStoredRouteHash(hash: string | null | undefined): string | null {
  const route = parseDesktopRestorableRoute(hash);
  if (!route) {
    return null;
  }
  const primaryCloudEnvironmentId = readDesktopPrimaryCloudEnvironmentId();
  if (!primaryCloudEnvironmentId || route.kind === "raw") {
    return stringifyDesktopRestorableRoute(route);
  }
  if (
    route.environmentId !== primaryCloudEnvironmentId &&
    route.environmentId !== DESKTOP_PRIMARY_CLOUD_ENV_PLACEHOLDER
  ) {
    return stringifyDesktopRestorableRoute(route);
  }
  return stringifyDesktopRestorableRoute({
    ...route,
    environmentId: DESKTOP_PRIMARY_CLOUD_ENV_PLACEHOLDER,
  });
}

function resolveDesktopStoredRouteHash(hash: string | null | undefined): string | null {
  const route = parseDesktopRestorableRoute(hash);
  if (!route) {
    return null;
  }
  const primaryCloudEnvironmentId = readDesktopPrimaryCloudEnvironmentId();
  if (!primaryCloudEnvironmentId || route.kind === "raw") {
    return stringifyDesktopRestorableRoute(route);
  }
  if (
    route.environmentId !== DESKTOP_PRIMARY_CLOUD_ENV_PLACEHOLDER &&
    route.environmentId !== primaryCloudEnvironmentId &&
    isKnownDesktopSavedEnvironmentId(route.environmentId)
  ) {
    return stringifyDesktopRestorableRoute(route);
  }
  // Desktop relaunch only rehydrates the primary environment synchronously.
  // Cloud runtime ids rotate between launches, so restoring server-scoped
  // routes against the previous env id strands the chat surface behind a blank
  // pane until the user manually refreshes or reselects a thread.
  return stringifyDesktopRestorableRoute({
    ...route,
    environmentId: primaryCloudEnvironmentId,
  });
}

function readStoredDesktopRouteHash(): string | null {
  if (!isDesktopRenderer() || typeof window === "undefined") {
    return null;
  }
  try {
    return resolveDesktopStoredRouteHash(window.localStorage.getItem(DESKTOP_LAST_ROUTE_KEY));
  } catch {
    return null;
  }
}

function writeStoredDesktopRouteHash(hash: string): void {
  if (!isDesktopRenderer() || typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(DESKTOP_LAST_ROUTE_KEY, hash);
  } catch {
    // localStorage unavailable — silently ignore
  }
}

export function rememberDesktopRoute(hash: string = window.location.hash): void {
  const normalized = normalizeDesktopStoredRouteHash(hash);
  if (!normalized) {
    return;
  }
  writeStoredDesktopRouteHash(normalized);
}

export function restoreDesktopRouteOnBoot(): void {
  if (!isDesktopRenderer() || typeof window === "undefined") {
    return;
  }
  if (!hasBootstrappedDesktopHash(window.location.hash)) {
    return;
  }
  const restoredHash = readStoredDesktopRouteHash();
  if (!restoredHash || restoredHash === window.location.hash) {
    return;
  }
  window.location.hash = restoredHash;
}

export function restoreDesktopRouteAfterAuth(): boolean {
  if (!isDesktopRenderer() || typeof window === "undefined") {
    return false;
  }
  const restoredHash = readStoredDesktopRouteHash();
  if (!restoredHash) {
    return false;
  }
  const nextUrl = `${window.location.pathname}${window.location.search}${restoredHash}`;
  window.location.replace(nextUrl);
  return true;
}

export function reconcileDesktopPrimaryRouteEnvironmentId(input: {
  readonly sourceEnvironmentId: string;
  readonly targetEnvironmentId: string;
}): boolean {
  if (
    !isDesktopRenderer() ||
    typeof window === "undefined" ||
    input.sourceEnvironmentId === input.targetEnvironmentId
  ) {
    return false;
  }

  const route = parseDesktopRestorableRoute(window.location.hash);
  if (!route || route.kind === "raw" || route.environmentId !== input.sourceEnvironmentId) {
    return false;
  }

  window.location.hash = stringifyDesktopRestorableRoute({
    ...route,
    environmentId: input.targetEnvironmentId,
  });
  return true;
}

export const __desktopRoutePersistenceTestUtils = {
  cloudPrimaryEnvironmentPlaceholder: DESKTOP_PRIMARY_CLOUD_ENV_PLACEHOLDER,
  parseDesktopRestorableRoute,
  normalizeDesktopRestorableHash,
  normalizeDesktopStoredRouteHash,
  resolveDesktopStoredRouteHash,
  hasBootstrappedDesktopHash,
  readStoredDesktopRouteHash,
  storageKey: DESKTOP_LAST_ROUTE_KEY,
};
